/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
/**
 * Calculates the optimal width for a table column to minimize empty white space.
 *
 * This function determines the column width that balances content visibility with table compactness.
 * It handles outliers (very long text entries) while ensuring the majority of content fits comfortably.
 *
 * @param data - Array of data objects
 * @param getter - Function to extract text content from each data object
 * @param options - Configuration options
 * @param options.minAcceptable - Minimum acceptable width (default: 40)
 * @param options.maxAcceptable - Maximum acceptable width
 * @param options.padding - Additional padding to add (default: 0)
 * @param options.outlierMultiplier - Multiplier for outlier detection (default: 3)
 * @param options.outlierThreshold - Percentage threshold for outlier trimming (default: 0.1)
 * @param options.basePadding - Base padding added to all columns (default: 26)
 * @returns The calculated optimal width
 */
export function calculateOptimalColumnWidth<T>(
  data: T[],
  getter: (row: T) => string,
  {
    minAcceptable = 40,
    maxAcceptable,
    padding = 0,
    outlierMultiplier = 1.5,
    outlierThreshold = 0.1,
    basePadding = 26,
  }: {
    minAcceptable?: number;
    maxAcceptable?: number;
    padding?: number;
    outlierMultiplier?: number;
    outlierThreshold?: number;
    basePadding?: number;
  } = {},
): number {
  if (!data || data.length === 0) {
    return minAcceptable;
  }

  const values = data.map(getter).filter((text): text is string => text != null && text !== '');

  if (values.length === 0) {
    return minAcceptable;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Fallback: estimate width based on character count
    const maxLength = Math.max(...values.map((text) => text.length));
    return Math.max(minAcceptable, maxLength * 8 + basePadding + padding);
  }

  ctx.font = '14px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  const measuredValues = values.map((text) => ({
    text,
    width: ctx.measureText(text).width,
  }));

  const sorted = measuredValues.sort((a, b) => a.width - b.width);

  // Calculate median width
  const medianIndex = Math.floor((sorted.length - 1) / 2);
  const medianWidth = sorted[medianIndex].width;

  // Find extreme outliers using configurable multiplier
  const extremeOutlierThreshold = medianWidth * outlierMultiplier;
  const extremeOutliers = sorted.filter((item) => item.width > extremeOutlierThreshold);

  let trimmed = sorted;

  // Trim outliers if they exceed the threshold percentage
  const extremeOutlierPercentage = extremeOutliers.length / sorted.length;
  if (extremeOutlierPercentage > outlierThreshold) {
    trimmed = sorted.filter((item) => item.width <= extremeOutlierThreshold);
  }

  const maxWidth = Math.max(...trimmed.map((item) => item.width)) + basePadding + padding;
  if (maxAcceptable && maxWidth > maxAcceptable) {
    return maxAcceptable;
  }

  if (minAcceptable && maxWidth < minAcceptable) {
    return minAcceptable;
  }

  return maxWidth;
}
